import React, { useCallback, useRef, useLayoutEffect, useMemo, useEffect, useState } from 'react';
import Animated, { runOnJS, withTiming, useSharedValue, interpolateColor, useAnimatedStyle, useAnimatedReaction, useAnimatedScrollHandler, } from 'react-native-reanimated';
import { ScrollView, View } from 'react-native';
import { FlashList } from '@shopify/flash-list';
import Text from 'design-system/Text';
import StatusBar from 'design-system/StatusBar';
import ListLoader from 'design-system/ListLoader';
import SectionHeader from 'design-system/SectionHeader';
import SearchBar from 'design-system/SearchBar';
import { AppView, NavigationHeader, Spacer } from 'design-system';
import { rem, SCREEN_BOTTOM_PADDING, SCROLL_INDICATOR_INSETS } from 'design-system/values';
import SelectorControllableReanimated from 'design-system/SelectorControllableReanimated';
import useColors from 'hooks/useColors';
import useStyles from 'hooks/useStyles';
import { useAppDispatch } from 'store/hooks';
import { getSearchResults } from 'actions/invest';
import createStyleSheets from 'utils/createStyleSheets';
import { useMarginBottom } from 'hooks/useMarginBottom';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import { useMainStackNavigation, useMainStackRoute } from 'utils/types/navigationV6';
import { assertUnreachable } from 'utils/types';
import NoSearchResults from './NoSearchResults';
import StockSearchResult from './StockSearchResult';
import FundPickerFloatingBar from './FundPickerFloatingBar';
import SecclRecentlyViewedSection from './SecclRecentlyViewedSection';
import useFundPickerStore from '../hooks/useFundPickerStore';
import { useGetFilteredFunds } from '../hooks/useGetFilteredFunds';
import { isWeb } from '../../../constants';
const strings = {
    aarInfo: 'Average annual return (AAR) shows how much an investment has grown on average each year over a given period.',
};
const AnimatedFlashList = Animated.createAnimatedComponent(FlashList);
const minHeaderWidth = rem(51 + 92 + 122 + 97 + 3 * 8 + 2 * 16);
const keyExtractor = (item, index) => {
    if (item.id !== undefined)
        return `${item.id}-${index}`;
    const { itemType } = item;
    switch (itemType) {
        case 'recentlyViewedAssetIds':
            return '@recentlyViewedAssetIds';
        case 'sectionTitle':
            return `@sectionTitle[${item.sectionTitle}]`;
        default:
            assertUnreachable(itemType);
            return index.toString();
    }
};
const SearchStocksScreen = () => {
    const colors = useColors();
    const { active: fundPickerMode } = useFundPickerStore();
    const dispatch = useAppDispatch();
    const styles = useStyles(styleSet);
    const navigation = useMainStackNavigation();
    const route = useMainStackRoute();
    const [searchTerm, setSearchTerm] = useState('');
    const [isInputFocused, setIsInputFocused] = useState(false);
    const [searchCategory, setSearchCategory] = useState('All');
    const scrollX = useSharedValue(0);
    const scrollY = useSharedValue(0);
    const searchBarRef = useRef(null);
    const selectorScrollViewRef = useRef(null);
    const listRef = useRef(null);
    const { paddingHorizontalStyle, paddingHorizontal, deviceWidth, width: WIDTH } = useAppFrameDimensions(isWeb);
    const shouldHeaderScroll = useMemo(() => WIDTH < minHeaderWidth, [WIDTH]);
    const { searchResults, hasLoadedFunds, hasRecentlyViewedFunds } = useGetFilteredFunds(searchCategory, searchTerm, isInputFocused);
    useEffect(() => {
        if (!hasLoadedFunds) {
            dispatch(getSearchResults('seccl'));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const sections = useMemo(() => [
        { name: 'All', width: rem(51) },
        { name: 'Low risk', width: rem(91) },
        { name: 'Medium risk', width: rem(120) },
        { name: 'High risk', width: rem(94) },
    ].map((item) => ({
        ...item,
        id: item.name,
        text: item.name,
    })), []);
    useLayoutEffect(() => {
        navigation.setOptions({
            title: 'Funds',
            headerTintColor: colors.text.primary,
        });
    }, [colors.background.light, colors.text.primary, route.params]);
    const contentContainerStyle = useMarginBottom('paddingBottom', SCREEN_BOTTOM_PADDING, paddingHorizontalStyle, styles.content);
    const renderItem = useCallback(({ item, index }) => {
        if (item.id === undefined) {
            const { itemType } = item;
            switch (itemType) {
                case 'recentlyViewedAssetIds':
                    return <SecclRecentlyViewedSection />;
                case 'sectionTitle': {
                    const isFirstIndex = index === 0;
                    return (<SectionHeader horizontalMargins largeVerticalMargins style={isFirstIndex ? styles.firstSectionHeader : null}>
                {item.sectionTitle}
              </SectionHeader>);
                }
                default:
                    assertUnreachable(itemType);
                    return null;
            }
        }
        return <StockSearchResult data={item}/>;
    }, [styles.firstSectionHeader]);
    const textInputStyle = useAnimatedStyle(() => ({
        backgroundColor: interpolateColor(scrollY.value, [0, 40], [
            colors.cards.onDark,
            colors.background.dark,
        ]),
    }), [colors.background.dark, colors.cards.onDark, scrollY]);
    const scrollHandler = useAnimatedScrollHandler((handle) => {
        scrollY.value = handle.contentOffset.y;
    }, []);
    const centerOnItem = React.useCallback((index) => {
        if (selectorScrollViewRef.current && sections[index]) {
            const item = sections[index];
            const itemsX = sections.slice(0, index).reduce((acc, sec) => acc + sec.width + rem(4), 0);
            const itemWidth = item.width;
            const scrollViewWidth = deviceWidth - (paddingHorizontal + rem(16)) * 2;
            let scrollToX = itemsX - scrollViewWidth / 2 + itemWidth / 2;
            const totalContentWidth = sections.reduce((acc, sec) => acc + sec.width, 0) + Math.max(0, sections.length - 1) * rem(4);
            const maxScrollX = Math.max(0, totalContentWidth - scrollViewWidth + rem(32));
            scrollToX = Math.max(0, Math.min(scrollToX, maxScrollX));
            selectorScrollViewRef.current.scrollTo({
                x: scrollToX,
                animated: true,
            });
        }
    }, [sections, deviceWidth, paddingHorizontal]);
    useAnimatedReaction(() => Math.round(scrollX.value / deviceWidth), (currentPage, previousPage) => {
        if (shouldHeaderScroll) {
            if (currentPage !== previousPage) {
                runOnJS(centerOnItem)(currentPage);
            }
        }
    });
    const setSelection = useCallback((index) => {
        scrollX.value = withTiming(index * deviceWidth);
        setSearchCategory(sections[index].text);
    }, [deviceWidth, scrollX, sections]);
    // On web the risk filters swap the list in place, so a shorter list would otherwise open mid-page
    useEffect(() => {
        if (isWeb) {
            listRef.current?.scrollToOffset({ offset: 0, animated: false });
        }
    }, [searchCategory]);
    const ListEmptyComponent = useMemo(() => {
        if (hasLoadedFunds) {
            return <NoSearchResults broker="seccl"/>;
        }
        return <ListLoader smallMargin/>;
    }, [hasLoadedFunds]);
    const ListHeaderComponent = useMemo(() => isInputFocused ? null : (<Text TextThin-16 Secondary style={styles.infoText}>
          {strings.aarInfo}
        </Text>), [styles.infoText, isInputFocused]);
    const handleFocus = useCallback(() => {
        setIsInputFocused(true);
    }, []);
    const handleBlur = useCallback(() => {
        setIsInputFocused(false);
    }, []);
    return (<AppView withFrame={false}>
      <StatusBar barStyle="dark-content" animated/>
      <View style={styles.flex}>
        <NavigationHeader scrollY={scrollY}>
          <View style={paddingHorizontalStyle}>
            <SearchBar noDebounce ref={searchBarRef} onBlur={handleBlur} onFocus={handleFocus} placeholder={'Search funds'} containerStyle={styles.searchBar} textInputBgStyles={textInputStyle} onChangeSearchTerm={setSearchTerm}/>
          </View>
          {!searchTerm && (isInputFocused ? !hasRecentlyViewedFunds : true) ? (<>
              <Spacer h={8}/>
              <ScrollView horizontal ref={selectorScrollViewRef} scrollEnabled={shouldHeaderScroll} showsHorizontalScrollIndicator={false} contentContainerStyle={{
                paddingHorizontal: paddingHorizontal + rem(16),
            }}>
                <SelectorControllableReanimated scrollX={scrollX} sections={sections} screenWidth={deviceWidth} setSelection={setSelection} selectorStyle={styles.selectorStyle} itemContainerStyle={styles.itemContainerStyle}/>
              </ScrollView>
              <Spacer h={8}/>
            </>) : null}
        </NavigationHeader>
        <AnimatedFlashList ref={listRef} data={searchResults} renderItem={renderItem} onScroll={scrollHandler} scrollEventThrottle={16} onEndReachedThreshold={0.8} keyExtractor={keyExtractor} showsVerticalScrollIndicator keyboardDismissMode="on-drag" keyboardShouldPersistTaps="handled" ListEmptyComponent={ListEmptyComponent} ListHeaderComponent={ListHeaderComponent} contentContainerStyle={contentContainerStyle} scrollIndicatorInsets={SCROLL_INDICATOR_INSETS}/>
      </View>
      {fundPickerMode ? <FundPickerFloatingBar popCount={2}/> : null}
    </AppView>);
};
const styleSet = createStyleSheets(() => ({
    flex: {
        flex: 1,
    },
    content: {
        paddingTop: rem(8),
    },
    searchBar: {
        paddingTop: rem(8),
    },
    itemContainerStyle: {
        margin: 0,
        padding: 0,
    },
    selectorStyle: {
        margin: 0,
        padding: 0,
        borderRadius: rem(8),
        justifyContent: 'center',
    },
    firstSectionHeader: {
        marginTop: rem(8),
    },
    infoText: {
        marginHorizontal: rem(16),
        marginBottom: rem(16),
    },
}));
export default SearchStocksScreen;
